<script setup>
import { ref, computed, watch, nextTick, defineComponent, h } from 'vue'
import { usePage } from '@inertiajs/vue3'
import Sidebar from '@pages/Shared/sidebar/StaffSidebar.vue'
import Loader from '@pages/Shared/AppLoader.vue'
import Nav from '@pages/Shared/navs/StaffNav.vue'
import SidebarItem from '@pages/Shared/StaffSidebarItem.vue'
import {
  PhHouse, PhUserCircle, PhStudent, PhBuilding, PhBank,
  PhBook, PhNotePencil, PhArticle, PhScroll,
  PhPersonSimpleRun, PhPencilSimple, PhCalendar,
  PhGraduationCap,
  PhUserList,
  PhMicrophone,
  PhClipboardText,
} from '@phosphor-icons/vue'

const openSidebar   = ref(false)
const toggleSidebar = () => { openSidebar.value = !openSidebar.value }
const closeSidebar  = () => { openSidebar.value = false }
const sidebarRef    = ref(null)

const page             = usePage()
const can              = computed(() => page.props?.can || {})
const currentComponent = computed(() => page?.component || '')
const isActive = (...pages) => pages.flat().includes(currentComponent.value)

const NairaIcon = defineComponent({
  props: { size: { type: Number, default: 18 } },
  render() {
    return h('svg', {
      xmlns: 'http://www.w3.org/2000/svg',
      width: this.size, height: this.size,
      viewBox: '0 0 24 24', fill: 'none',
      stroke: 'currentColor', 'stroke-width': '1.5',
      'stroke-linecap': 'round', 'stroke-linejoin': 'round',
    }, [
      h('path', { d: 'M7 19V5l10 14V5' }),
      h('path', { d: 'M5 9.5h14' }),
      h('path', { d: 'M5 13.5h14' }),
    ])
  },
})

watch(currentComponent, async () => {
  await nextTick()
  sidebarRef.value?.scrollActiveIntoView()
}, { immediate: true })

const sections = computed(() => [
  {
    title: 'General',
    items: [
      { title: 'Dashboard', url: '/staff/dashboard', icon: PhHouse, pages: ['Dashboard/StaffDashboard'] },
    ],
  },
  {
    title: 'Management',
    items: [
      { title: 'Sessions/Terms', url: '/staff/academic-sessions', icon: PhCalendar,        pages: ['staff/ManageSessions'],   permission: can.value.viewAnySession },
      { title: 'Staff',          url: '/staff/manage-primary',       icon: PhUserCircle,      pages: ['staff/ManageStaff'],      permission: can.value.viewAnyStaff },
      { title: 'Students',       url: '/staff/manage-students',    icon: PhStudent,         pages: ['staff/ManageStudents'],   permission: can.value.viewAnyStudent },
      { title: 'Sections',       url: '/staff/manage-sections',    icon: PhBuilding,        pages: ['staff/ManageSections'],   permission: can.value.viewAnySection },
      { title: 'Classes',        url: '/staff/manage-classes',     icon: PhBank,            pages: ['staff/ManageClasses'],    permission: can.value.viewAnyClass },
      { title: 'Subjects',       url: '/staff/manage-subjects',    icon: PhBook,            pages: ['staff/ManageSubjects'],   permission: can.value.viewAnySubject },
    ],
  },
  {
    title: 'Examination',
    items: [
      { title: 'Score-sheets', url: '/staff/score-sheets',       icon: PhArticle,         pages: ['staff/ScoreSheets'],       permission: can.value.viewAnyScoreSheet },
      { title: 'Assessments',  url: '/staff/manage-assessments', icon: PhNotePencil,      pages: ['staff/ManageAssessments'], permission: can.value.viewAnyAssessment },
      { title: 'Scores',       url: '/staff/manage-scores',      icon: PhPencilSimple,    pages: ['staff/ManageScores'] },
      { title: 'Results',      url: '/staff/manage-results',     icon: PhScroll,          pages: ['staff/ManageResults'],     permission: can.value.viewAnyResult },
      { title: 'Promotions',   url: '/staff/manage-promotions',  icon: PhPersonSimpleRun, pages: ['staff/ManagePromotions'],  permission: can.value.viewAnyPromotion },
    ],
  },
  {
    title: 'Admission',
    items: [
      { title: 'Admissions',   url: '/staff/manage-admissions',   icon: PhGraduationCap, pages: ['staff/Admissions/Index', 'staff/Admissions/Show', 'staff/Admissions/Print'], permission: can.value.viewAnyAdmission },
      { title: 'Applicants',   url: '/staff/manage-applicants',   icon: PhUserList,      pages: ['staff/Applicants/Index', 'staff/Applicants/Show'], permission: can.value.viewAnyApplicant },
      { title: 'Applications', url: '/staff/manage-applications', icon: PhClipboardText, pages: ['staff/Applications/Index', 'staff/Applications/Show'], permission: can.value.viewAnyApplication },
      { title: 'Interviews',   url: '/staff/manage-interviews',   icon: PhMicrophone,    pages: ['staff/Interviews/Index'], permission: can.value.viewAnyInterview },
      { title: 'Payments',     url: '/staff/manage-payments',     icon: NairaIcon,       pages: ['staff/Payments/Index', 'staff/Payments/Show'], permission: can.value.viewAnyPayment },
    ],
  },
])
</script>

<template>
  <div class="bg-background min-h-screen">

    <Sidebar ref="sidebarRef" :openSidebar="openSidebar" @close="closeSidebar">
      <template v-for="section in sections" :key="section.title">
        <p class="px-3 pb-2 text-[10px] font-bold uppercase tracking-widest text-white/30"
          :class="section.title === 'General' ? 'pt-1' : 'pt-4'">
          {{ section.title }}
        </p>
        <template v-for="item in section.items" :key="item.title">
          <SidebarItem
            v-if="item.permission === undefined || item.permission"
            :title="item.title"
            :url="item.url"
            :active="isActive(item.pages)"
          >
            <component :is="item.icon" :size="20" />
          </SidebarItem>
        </template>
      </template>
    </Sidebar>

    <Nav :sidebarOpen="openSidebar" @toggleSidebar="toggleSidebar" />

    <main class="pt-20 pb-10 px-6 md:ml-[250px] transition-all relative">
      <slot />
    </main>

    <Loader />
  </div>
</template>